import { GameConfig } from "@/config/GameConfig";
import { SPEED_TRIAL_FIXED_STEP_MS } from "@/core/SpeedEngine";

/**
 * PR26 LAST-SECOND TURN V3: a continuous, quadratic speed multiplier for
 * LAST-SECOND TURN specifically - deliberately separate from
 * `computeManeuverSpeedWeight()` (`maneuverSpeedWeight.ts`), which still
 * applies unchanged to every OTHER maneuver type. x1 at the run/mode's own
 * normal starting/base speed, rising to exactly x4 at 75ms
 * (`SPEED_TRIAL_FIXED_STEP_MS` - the same absolute floor SPEED TRIAL and
 * the Campaign post-95 RUSH continuation use elsewhere), never beyond x4.
 *
 * `progress = clamp((baseSpeedMs - persistentSpeedMs) / (baseSpeedMs - 75), 0, 1)`
 * `multiplier = min + (max - min) * progress^2`
 *
 * The quadratic term is intentional (locked design): most of the reward
 * increase arrives only once the snake is genuinely very fast, not early
 * in the run.
 *
 * `persistentSpeedMs` MUST be the run's real, persistent/accumulated
 * movement-speed target (`LevelGame.currentUnderlyingTargetStepMs()`) -
 * never the Speed Engine's interpolated `currentStepMs` and never SPEED
 * TRIAL's temporary fixed override. Both of those can read as exactly
 * `SPEED_TRIAL_FIXED_STEP_MS` (75ms) while SPEED TRIAL is active even for a
 * player who has made no real persistent progress at all - feeding either
 * of them here would let SPEED TRIAL "fake" a x4 reward the player hasn't
 * actually earned. `currentUnderlyingTargetStepMs()` is specifically the
 * value that composes Growth/RUSH debt/Daily pressure/persistent mode
 * boost only, capped by the NORMAL cap, and is exactly what the engine
 * returns to once SPEED TRIAL ends - see that method's own doc in
 * `LevelGame.ts`.
 *
 * `baseSpeedMs` MUST be the run/mode/level's OWN normal starting/base
 * speed (`LevelGame`'s own `baseStepMs`, sourced from each mode's
 * `LevelDefinition.baseStepMs` - a Campaign world's own value, a Daily
 * spec's own generated starting speed, or Endless's own configured value)
 * - never a hardcoded constant shared across every mode.
 *
 * Safe for any `baseSpeedMs <= SPEED_TRIAL_FIXED_STEP_MS` (an unusually
 * fast or misconfigured mode/level, where the denominator would be <= 0):
 * short-circuits to the MAX multiplier rather than dividing by a
 * non-positive number - such a mode's own "normal" speed is already at or
 * beyond the fastest speed this curve was designed around, so treating
 * ordinary play there as maximally fast is the sensible reading, not a
 * crash or an arbitrary fallback.
 */
export function computeLastSecondTurnSpeedMultiplier(baseSpeedMs: number, persistentSpeedMs: number): number {
  const { min, max } = GameConfig.scoring.lastSecondTurn.speedMultiplier;
  const denominator = baseSpeedMs - SPEED_TRIAL_FIXED_STEP_MS;
  if (denominator <= 0) return max;
  const progress = Math.min(1, Math.max(0, (baseSpeedMs - persistentSpeedMs) / denominator));
  return min + (max - min) * progress * progress;
}
